<template>
    <div class="ui-page">
        <ui-page-header title="Lista de Credores" subtitle="Listas de credores importadas para o QGC dos casos.">
            <v-btn v-if="$allowed('create')" id="btnNew" type="button" color="primary" depressed to="/lista-credores/incluir">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <div class="ui-list-bar">
            <ui-pills v-model="filters.status" :items="statusPills" @input="applyFilters" />
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="filters.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify" outlined dense hide-details
                    class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters" />
            </template>

            <v-data-table :headers="headers" :items="items" :loading="loading"
                :server-items-length="count" :options.sync="options" @update:options="load()" disable-sort hide-default-footer
                no-data-text="Nenhuma lista de credores encontrada" loading-text="Carregando dados...">

                <template v-slot:[`item.name`]="{ item }">
                    <a class="ui-cell-main import-link" @click.prevent="openImport(item)">{{ item.name }}</a>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="statusLabel(item.status)" :color="statusColor(item.status)" />
                </template>

                <template v-slot:[`item.client`]="{ item }">
                    <span>{{ item.client ? item.client.name : 'N/A' }}</span>
                </template>

                <template v-slot:[`item.client_company`]="{ item }">
                    <span>{{ getClientCompanyName(item) }}</span>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY") }}
                </template>

                <template v-slot:[`item.finished_at`]="{ item }">
                    <span v-if="item.finished_at && item.finished_at !== '0000-00-00 00:00:00'">
                        {{ item.finished_at | defaultDate("DD/MM/YYYY") }}
                    </span>
                    <span v-else>-</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        {
                            text: 'Ver importação',
                            icon: 'mdi-eye-outline',
                            handler: () => openImport(item),
                        },
                        {
                            text: 'Remover lista e créditos importados',
                            icon: 'mdi-delete-outline',
                            danger: true,
                            disabled: Number(item.status) !== 1 || !$check('Creditors', 'DELETE'),
                            handler: () => deleteItem(item),
                        },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="count" :page="options.page" :items-per-page="options.itemsPerPage"
                    :disabled="loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <v-dialog v-model="deleteDialog" max-width="720px" persistent>
            <v-card>
                <v-card-title>
                    <v-icon large class="mr-2 error--text">
                        mdi-alert-circle-outline
                    </v-icon>
                    Remover lista de credores
                </v-card-title>

                <v-card-text class="pt-4 black--text">
                    <div v-if="previewLoading" class="text-center py-8">
                        <v-progress-circular indeterminate color="primary" />
                        <div class="mt-3">Verificando os créditos da lista...</div>
                    </div>

                    <template v-else-if="removalPreview">
                        <p>
                            A lista <strong>{{ itemToDelete ? itemToDelete.name : '' }}</strong> será removida. Os
                            créditos mantidos exclusivamente por ela serão removidos ou restaurados ao estado anterior.
                        </p>

                        <v-alert v-if="removalPreview.mode === 'legacy'" type="warning" outlined dense>
                            Esta é uma lista antiga. Os créditos serão identificados pelo arquivo original,
                            considerando caso, empresa, CPF/CNPJ, classe, moeda e versão.
                        </v-alert>

                        <v-row dense class="mb-2">
                            <v-col cols="6" md="3">
                                <strong>Localizados</strong><br>{{ removalPreview.summary.matched }}
                            </v-col>
                            <v-col cols="6" md="3">
                                <strong>Removíveis</strong><br>{{ removalPreview.summary.removable }}
                            </v-col>
                            <v-col cols="6" md="3">
                                <strong>Bloqueados</strong><br>{{ removalPreview.summary.blocked }}
                            </v-col>
                            <v-col cols="6" md="3">
                                <strong>Não encontrados</strong><br>{{ removalPreview.summary.not_found }}
                            </v-col>
                        </v-row>

                        <v-alert v-if="removalPreview.summary.retained" type="info" outlined dense>
                            {{ removalPreview.summary.retained }} crédito(s) receberam uma importação posterior e
                            serão preservados com os dados mais recentes.
                        </v-alert>

                        <v-alert v-if="removalPreview.warnings && removalPreview.warnings.length"
                            type="warning" outlined dense>
                            {{ removalPreview.warnings.length }} crédito(s) foram alterados após a importação.
                            Como pertencem a esta lista, serão removidos ou restaurados ao estado anterior.
                        </v-alert>

                        <v-simple-table v-if="removalPreview.warnings && removalPreview.warnings.length" dense
                            class="mb-4">
                            <thead>
                                <tr>
                                    <th>Credor alterado</th>
                                    <th>CPF/CNPJ</th>
                                    <th>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="warning in removalPreview.warnings" :key="`warning-${warning.creditor_id}`">
                                    <td>{{ warning.name || `#${warning.creditor_id}` }}</td>
                                    <td>{{ warning.registration || '-' }}</td>
                                    <td>{{ warning.reasons.join('; ') }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>

                        <v-alert v-if="removalPreview.blockers && removalPreview.blockers.length"
                            type="error" outlined>
                            A remoção não pode ser concluída enquanto existirem os impedimentos abaixo.
                        </v-alert>

                        <v-simple-table v-if="removalPreview.blockers && removalPreview.blockers.length" dense>
                            <thead>
                                <tr>
                                    <th>Credor</th>
                                    <th>CPF/CNPJ</th>
                                    <th>Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="blocker in removalPreview.blockers" :key="blocker.creditor_id">
                                    <td>{{ blocker.name || `#${blocker.creditor_id}` }}</td>
                                    <td>{{ blocker.registration || '-' }}</td>
                                    <td>{{ blocker.reasons.join('; ') }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>

                        <v-alert v-else type="error" outlined dense class="mt-4">
                            Esta ação é irreversível.
                        </v-alert>
                    </template>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="closeDeleteDialog" :disabled="removing || previewLoading">
                        Cancelar
                    </v-btn>
                    <v-btn v-if="$allowed('delete')" color="red darken-1" text @click="confirmDelete" :loading="removing"
                        :disabled="previewLoading || !removalPreview || !removalPreview.can_remove">
                        Remover lista
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import creditorListStatus from "@/common/data/creditor-list-status.data";

export default {
    name: "CreditorsList",

    data: () => ({
        loading: false,
        initialLoad: true, 

        filters: {
            name: null,
            status: "",
        },

        statusOptions: [{ id: "", name: "Todos" }, ...creditorListStatus],

        headers: [
            { text: "Nome", value: "name" },
            { text: "Caso", value: "client" },
            { text: "Empresa", value: "client_company" },
            { text: "Criado em", value: "created_at" },
            { text: "Processado em", value: "finished_at" },
            { text: "Status", value: "status" },
            { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
        ],

        items: [],
        count: 0,
        
        options: {
            page: 1,
            itemsPerPage: 10,
            sortBy: [],
            sortDesc: [],
            groupBy: [],
            groupDesc: [],
            mustSort: false,
            multiSort: false
        },
        
        footerOptions: {
            itemsPerPageText: 'Itens por página:',
            itemsPerPageOptions: [10, 20, 30, 40, 50],
            pageText: '{0}-{1} de {2}',
        },

        deleteDialog: false,
        itemToDelete: null,
        previewLoading: false,
        removing: false,
        removalPreview: null,
    }),

    computed: {
        hasFilters() {
            return Object.values(this.filters).some((value) => value !== null && value !== undefined && value !== "");
        },

        statusPills() {
            const tones = { 0: "warning", 1: "success", 2: "info", 3: "danger" };
            return this.statusOptions.map((status) => ({
                value: status.id,
                text: status.name,
                tone: status.id === "" ? undefined : tones[status.id],
                count: this.filters.status === status.id ? this.count : undefined,
            }));
        },

        breadcrumbItems() {
            return [
                { text: "Home", to: "/home", exact: true },
                { text: "Lista de Credores", disabled: true },
            ];
        },
    },

    methods: {
        applyFilters() {
            this.load(true);
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.filters = {
                name: null,
                status: "",
            };
            this.applyFilters();
        },

        // O @update:options da tabela recarrega a lista.
        pageChanged(page) {
            this.options.page = page;
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.options.itemsPerPage = size;
        },

        async load(resetPage = false) {
            
            if (resetPage) {
                this.options.page = 1;
            }

            let search = Object.fromEntries(
                Object.entries(this.filters).filter(([_, v]) => v !== null && v !== undefined && v !== "")
            );
            
            search = Object.keys(search).length > 0 ? JSON.stringify(search) : null;

            this.loading = true;

            try {
                const result = await axios.get('/api/admin/creditor-lists', { 
                    params: { 
                        page: this.options.page, 
                        limit: this.options.itemsPerPage, 
                        search: search 
                    } 
                });

                const payload = result.data.content;

                this.items = [...(payload.data || [])]; 
                this.count = payload.total || 0; 

            } catch (error) {
                console.error('Erro ao carregar lista:', error);
                this.$dialog.notify.error(this.$codes(error) || "Erro ao buscar dados", { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        statusColor(status) {
            switch (status) {
                case 0: return 'warning';
                case 1: return 'accent';
                case 2: return 'info';
                case 3: return 'error';
                default: return 'grey';
            }
        },

        statusLabel(status) {
            const foundStatus = this.statusOptions.find(s => s.id === status);
            return foundStatus ? foundStatus.name : "Desconhecido";
        },

        // Abre a importacao: linhas importadas e planilha original.
        openImport(item) {
            this.$router.push(`/lista-credores/${item.id}`);
        },

        getClientCompanyName(item) {
            const company = item.client_company || item.clientCompany;
            return company && company.name ? company.name : "Principal";
        },

        async deleteItem(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
            this.removalPreview = null;
            this.previewLoading = true;

            try {
                const result = await axios.get(`/api/admin/creditor-lists/${item.id}/removal-preview`);
                this.removalPreview = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || "Erro ao verificar a remoção da lista", {
                    position: 'top-right',
                    timeout: 5000
                });
                this.closeDeleteDialog();
            } finally {
                this.previewLoading = false;
            }
        },

        closeDeleteDialog() {
            this.deleteDialog = false;
            this.itemToDelete = null;
            this.removalPreview = null;
        },

        async confirmDelete() {
            if (!this.itemToDelete || !this.removalPreview || !this.removalPreview.can_remove) return;

            this.removing = true;

            try {
                const result = await axios.delete(`/api/admin/creditor-lists/${this.itemToDelete.id}`);
                const content = result.data.content || {};

                this.$dialog.notify.info(
                    content.storage_cleanup_warning || "Lista e dados importados removidos com sucesso",
                    { position: "top-right", timeout: 4000 }
                );
                
                this.closeDeleteDialog();
                await this.load();

            } catch (error) {
                const content = error.response && error.response.data && error.response.data.content;
                if (error.response && error.response.status === 409 && content) {
                    this.removalPreview = {
                        mode: content.mode,
                        can_remove: false,
                        summary: content.summary,
                        blockers: content.blockers || [],
                        warnings: content.warnings || []
                    };
                }
                this.$dialog.notify.error(this.$codes(error) || "Erro ao excluir", { position: 'top-right', timeout: 2000 });
            } finally {
                this.removing = false;
            }
        },
    },
};
</script>
